import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGetRequestHelpdeskUrl } from '../../helpers/api';
import Loader from '../../components/Loader/Loader';
import { selectFrappeRequestData, setFrappeRequestData } from '../../redux/slicers/activitySlicer';
import { useDispatch, useSelector } from 'react-redux';
import { checkIfEmailIsVerifiedOrNot, useUserContext } from '../../contexts/UserContext';
import { warningToast } from '../../helpers/toastHelper';
import { NoDataFound } from '../../common';
import {
  getLocalStorage,
  openFormInCaseOfNewOrgAndFreemiumUser,
  removeLocalStorage,
  setLocalStorage,
  setTitle,
  windowOpen,
} from '../../helpers/jsHelper';
import { AUTH_TOKEN, CURRENT_USER } from '../../constants/projectConstants';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useStaticPermissions } from '../../hooks/usePermissions';
import { PERMISSION } from '../../constants/permissionConstants';
import Requests from './index.js';
import CustomTab from '../../common/Tab/index.js';
import {
  ADMIN_MY_PRODUCTS_3DMODAL_EACH,
  REQUESTS_ROUTE,
  REQUESTS_ROUTE_MODELS,
  REQUESTS_ROUTE_PDP,
} from '../../constants/routePathConstants.js';
import { REQUESTS_MENU_TAB } from '../../constants/constants.js';
import { MxEvent } from '../../common/Mixpanel.js';
import { My3dModelDetailsPopupController } from '../../common/My3dModelDetailsPopup/My3dModelDetailsPopup.js';
import NotFound from '../NotFound.js';
import styles from './requests.module.scss';
import { navigate } from '../../helpers/projectHelper.js';
import { isLocalhost, showRequestTabs } from './Sections/helper.js';
import { LogConsole } from '../../helpers/logEvents.js';

// Constants
const RETRY_ATTEMPTS = 2;
const RETRY_DELAY_BASE = 1000; // 1 second
const LOADING_AUTO_CLOSE_DELAY = 300000; // 5 minutes
const RELOGIN_DELAY = 2000; // 2 seconds

const tabRouteList = {
  models: REQUESTS_ROUTE_MODELS,
  pdp: REQUESTS_ROUTE_PDP,
};

let timeout = null;

const RequestTabs = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, isGuestUser } = useUserContext();
  const prevOrgRef = useRef(null);
  const isRequestsPage = showRequestTabs();
  const [loader, setLoader] = useState(true);
  // Extract permission props
  const {
    hasProductPageRequestPermission = false,
    hasRequestV2PagePermission = false,
    isUpdatingUserDetails = false,
    show = false,
  } = props;

  // Determine default route based on current location and permissions
  const defaultRoute = useMemo(() => {
    // If on PDP route and has product page permission, stay on PDP
    if (location.pathname.includes(tabRouteList.pdp) && hasProductPageRequestPermission) {
      return REQUESTS_MENU_TAB.PDP.eventKey;
    }
    // If on Models route and has request v2 permission, stay on Models
    if (location.pathname.includes(tabRouteList.models) && hasRequestV2PagePermission) {
      return REQUESTS_MENU_TAB.MODELS.eventKey;
    }
    // Default based on available permissions
    if (hasRequestV2PagePermission) {
      return REQUESTS_MENU_TAB.MODELS.eventKey;
    }
    if (hasProductPageRequestPermission) {
      return REQUESTS_MENU_TAB.PDP.eventKey;
    }
    // Fallback to Models
    return REQUESTS_MENU_TAB.MODELS.eventKey;
  }, [location.pathname, hasRequestV2PagePermission, hasProductPageRequestPermission]);

  const [activeTab, setActiveTab] = useState(defaultRoute);

  useEffect(() => {
    // this is needed due to flickering of page
    if (timeout) clearTimeout(timeout);
    if (!isUpdatingUserDetails) {
      timeout = setTimeout(() => setLoader(false), 500);
    } else {
      setLoader(true);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isUpdatingUserDetails]);

  // Clear URL params when organization changes
  useEffect(() => {
    if (prevOrgRef.current && prevOrgRef.current !== currentUser?.organization) {
      // Organization has changed, clear URL params
      setSearchParams({}, { replace: true });
    }
    prevOrgRef.current = currentUser?.organization;
  }, [currentUser?.organization, setSearchParams]);

  // Redirect to PDP tab only when inside Requests routes and query params are present
  useEffect(() => {
    const id = searchParams.get('id');
    const organization = searchParams.get('organization');
    const isRequestsRoute = location.pathname.includes(REQUESTS_ROUTE);
    if (isRequestsRoute && (id || organization) && !location.pathname.includes(tabRouteList.pdp)) {
      // Redirect to PDP tab within Requests section with the same query params
      navigate(`${tabRouteList.pdp}?${searchParams.toString()}`, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);

  const tabList = useMemo(
    () => [
      {
        eventKey: REQUESTS_MENU_TAB.MODELS.eventKey,
        title: REQUESTS_MENU_TAB.MODELS.title,
        route: tabRouteList.models,
        component: isRequestsPage ? <Requests {...props} /> : null,
        permission: PERMISSION.REQUEST_V2_PAGE,
        mx_event: MxEvent.VISITED_REQUESTS_MODELS,
        tabContentStyle: { paddingTop: 16 },
        tabContentClassName: 'position-relative',
        canGuestUserAccess: true,
        tabStyle:
          !isUpdatingUserDetails &&
          isRequestsPage &&
          !loader &&
          hasRequestV2PagePermission &&
          hasProductPageRequestPermission
            ? { display: 'block' }
            : { display: 'none' },
      },
      {
        eventKey: REQUESTS_MENU_TAB.PDP.eventKey,
        title: REQUESTS_MENU_TAB.PDP.title,
        route: tabRouteList.pdp,
        component: <RequestsV2UI {...props} />,
        permission: PERMISSION.CAN_CREATE_PRODUCT_PAGE_REQUEST,
        mx_event: MxEvent.VISITED_REQUESTS_PDP,
        tabContentStyle: { paddingTop: 16, height: 'calc(100vh - 132px)' },
        tabContentClassName: 'position-relative',
        tabStyle:
          !isUpdatingUserDetails &&
          !loader &&
          isRequestsPage &&
          hasRequestV2PagePermission &&
          hasProductPageRequestPermission
            ? { display: 'block' }
            : { display: 'none' },
      },
    ],
    [props, loader, hasProductPageRequestPermission, hasRequestV2PagePermission]
  );

  const UPDATED_TAB_MENU = useMemo(() => {
    const list = isGuestUser() ? tabList.filter((a) => a?.canGuestUserAccess) : tabList;
    return [...list.filter(Boolean)];
  }, [
    currentUser?.organization,
    currentUser?.permissions_list,
    currentUser?.organization_permissions_list,
    tabList,
    isGuestUser,
  ]);

  const handleActiveTab = (val) => {
    setActiveTab(val);
  };

  // Always render CustomTab regardless of permissions
  // Filter tabs based on permissions inside UPDATED_TAB_MENU
  return (
    <div className={`h-100 w-100 position-relative ${!isRequestsPage ? 'd-none' : ''}`}>
      {isRequestsPage &&
        !isUpdatingUserDetails &&
        !hasProductPageRequestPermission &&
        !loader &&
        !hasRequestV2PagePermission && (
          <NotFound
            hidePageTitle={true}
            hideButton={true}
            message="You don't have any request assigned"
            className="vh-100"
          />
        )}
      <CustomTab
        tabList={UPDATED_TAB_MENU}
        defaultActive={defaultRoute}
        handleActiveTab={handleActiveTab}
        className={`tab-main-container libraryContainer ${
          activeTab === REQUESTS_MENU_TAB.PDP.eventKey ? styles.pdpTabContainer : ''
        }`}
        style={
          isRequestsPage &&
          !isUpdatingUserDetails &&
          !loader &&
          show &&
          (hasProductPageRequestPermission || hasRequestV2PagePermission)
            ? { display: 'block' }
            : { display: 'none' }
        }
        unmountOnExit={false}
      />
      {isRequestsPage && (isUpdatingUserDetails || loader) && (
        <Loader
          loading={true}
          position="absolute"
          style={{
            backgroundColor: '#F6F6F6',
          }}
        />
      )}
    </div>
  );
};

function RequestsV2({ preload = false, ...rest } = {}) {
  const { isUpdatingUserDetails, isGuestUser } = useUserContext();
  const { hasPermission } = useStaticPermissions();
  const [hasProductPageRequestPermission, setHasProductPageRequestPermission] = useState(false);
  const [hasRequestV2PagePermission, setHasRequestV2PagePermission] = useState(false);
  const enterpriseFormOpenedRef = useRef(false);
  const [logout, setLogout] = useState(false);
  const isRequestsPage = showRequestTabs();

  useEffect(() => {
    const run = async () => {
      if (isUpdatingUserDetails) return;

      setLogout(false);
      let currentUser = getLocalStorage(CURRENT_USER);
      currentUser = currentUser ? JSON.parse(currentUser) : {};
      const permissionsMap = currentUser?.permissions_list?.reduce((acc, p) => {
        acc[p.codename] = p;
        return acc;
      }, {});
      const organizationPermissionsMap = currentUser?.organization_permissions_list?.reduce(
        (acc, p) => {
          acc[p.codename] = p;
          return acc;
        },
        {}
      );

      const _hasProductPageRequestPermission = hasPermission(
        PERMISSION.CAN_CREATE_PRODUCT_PAGE_REQUEST,
        permissionsMap
      );
      const _hasRequestV2PagePermission = hasPermission(
        PERMISSION.REQUEST_V2_PAGE,
        organizationPermissionsMap
      );

      if (!_hasProductPageRequestPermission) {
        // set data to component to tell iframe to logout
        setLogout(true);
      }

      if (
        !_hasProductPageRequestPermission &&
        !_hasRequestV2PagePermission &&
        !enterpriseFormOpenedRef.current &&
        isRequestsPage &&
        !isGuestUser()
      ) {
        enterpriseFormOpenedRef.current = true;
        openFormInCaseOfNewOrgAndFreemiumUser({
          onCancelClick: () => {
            enterpriseFormOpenedRef.current = false;
          },
        });
      }

      setHasProductPageRequestPermission(_hasProductPageRequestPermission);
      setHasRequestV2PagePermission(_hasRequestV2PagePermission || isGuestUser());
    };

    run();
  }, [isUpdatingUserDetails]);

  // Load the component only if preload is true (set by AdminGuard).
  // If not preloading, return null to avoid reloading on route changes.
  if (!preload) return null;

  // if (isUpdatingUserDetails) {
  //   return <Loader loading={true} position="absolute" />;
  // }

  // Old conditional rendering logic - commented out
  // if (!hasProductPageRequestPermission && hasRequestV2PagePermission) {
  //   return <Requests {...rest} />;
  // }

  // if (hasProductPageRequestPermission && !hasRequestV2PagePermission) {
  //   return <RequestsV2UI {...rest} />;
  // }

  // if (hasProductPageRequestPermission && hasRequestV2PagePermission) {
  //   return <RequestTabs {...rest} />;
  // }

  // return (
  //   <NotFound
  //     hidePageTitle={true}
  //     hideButton={true}
  //     message="You don't have any request assigned"
  //     className="vh-100"
  //   />
  // );

  // New approach: Always render RequestTabs, let it handle permission-based visibility internally
  return (
    <>
      <RequestTabs
        {...rest}
        isUpdatingUserDetails={isUpdatingUserDetails}
        hasProductPageRequestPermission={hasProductPageRequestPermission}
        hasRequestV2PagePermission={hasRequestV2PagePermission}
        logout={logout}
      />
    </>
  );
}

export default RequestsV2;

let debounceTimer = null;
export const sendDataToFrappeApp = (data = {}) => {
  // Clear any existing timer
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Set new timer with 300ms debounce
  debounceTimer = setTimeout(() => {
    const frappeFrame = document.querySelector('#FRAPPE_APP');
    const params = new URLSearchParams(window.location.search);

    if (frappeFrame) {
      const win = frappeFrame?.contentWindow;
      win?.postMessage?.(
        {
          token: getLocalStorage(AUTH_TOKEN),
          origin: window.location.origin,
          openedRequestId: params.get('id'),
          source: 'APP_V2',
          theme: 'light',
          ...data,
        },
        frappeFrame.src
      );
    }
  }, 300);
};

export const RequestsV2UI = ({ show = true, logout = false, openedRequestId = null } = {}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser, isGuestUser, isUpdatingUserDetails } = useUserContext();
  const { hasPermission } = useStaticPermissions();
  const currentOrg = currentUser?.organization;
  const isReloggingInitiated = useRef(false);
  // Track which org current URL belongs to; avoids extra state re-renders
  const isFetchingData = useRef(false);
  const apiCallAttemptedRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const iframeLoadedRef = useRef(false);
  // Track last redirect target to avoid duplicate executions
  const lastRedirectRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageTitle, setPageTitle] = useState('Requests');
  const lastRequestIdRef = useRef(null);
  const frappeRequestData = useSelector(selectFrappeRequestData);
  const { url } = frappeRequestData;
  const urlRef = useRef(url);
  const isRequestsPage = showRequestTabs();
  const [iframeHeight, setIframeHeight] = useState(null);

  useEffect(() => {
    const [navigation] = performance.getEntriesByType('navigation');
    if (navigation?.type === 'reload') {
      // LogConsole('Clearing frappe data after reloading the page');
      clearDataAfterLogout();
    }
  }, []);

  // Check for URL param changes and notify iframe
  useEffect(() => {
    if (!openedRequestId || !iframeLoadedRef.current) return;
    sendDataToFrappeApp({
      openedRequestId,
    });
  }, [openedRequestId, iframeLoadedRef.current]);

  useEffect(() => {
    if (logout) {
      clearDataAfterLogout();
    }
  }, [logout]);

  const setUrl = (newUrl) => {
    urlRef.current = newUrl;
    // _setUrl(newUrl);
    dispatch(setFrappeRequestData({ url: newUrl }));
  };

  const fetchData = useCallback(async () => {
    // Check permission before proceeding
    const hasProductPageRequestPermission = hasPermission(
      PERMISSION.CAN_CREATE_PRODUCT_PAGE_REQUEST
    );

    // Prevent duplicate or repeated fetches
    if (!hasProductPageRequestPermission || isFetchingData.current || apiCallAttemptedRef.current) {
      return;
    }

    // Set wrapper position (cache DOM query)
    const pageWrapper = document.querySelector('.body-of-main .page-wrapper');
    if (pageWrapper) {
      pageWrapper.style.position = 'relative';
    }

    // Check if user is already logged in for current org via localStorage
    const FRAPPE_APP_URL = getLocalStorage('FRAPPE_APP_URL');
    const FRAPPE_FETCHED_ORG = getLocalStorage('FRAPPE_FETCHED_ORG', true);
    const FRAPPE_APP_READY = getLocalStorage('FRAPPE_APP_READY', true);

    if (FRAPPE_APP_READY && FRAPPE_APP_URL && FRAPPE_FETCHED_ORG === currentOrg) {
      // Only dispatch if Redux doesn't already have the URL to avoid redundant updates
      if (!urlRef.current) {
        setLoading(true);
        setUrl(FRAPPE_APP_URL);
      }
      // sendDataToFrappeApp will be called by iframe.onLoad
      apiCallAttemptedRef.current = true;
      return;
    }

    isFetchingData.current = true;
    apiCallAttemptedRef.current = true;
    isReloggingInitiated.current = false;
    setLoading(true);
    try {
      const response = await apiGetRequestHelpdeskUrl();

      // Check if response is ok
      if (!response.ok) {
        const status = response.status;
        const isRetryableError = [400, 401, 403].includes(status);

        // LogConsole(`API returned ${status}, isRetryable: ${isRetryableError}`);

        // If not retryable, set error and return
        if (!isRetryableError) {
          throw new Error(`HTTP ${status} - ${response.statusText}`);
        }

        // If retryable, throw to be caught and retry
        throw new Error(`HTTP ${status} - Retryable error`);
      }

      setError(false);
      const data = await response.json();
      const login_url = isLocalhost()
        ? data?.message?.login_url?.replace('https://', 'http://')
        : data?.message?.login_url;
      const url_break = new URL(login_url);
      if (url_break) {
        setLocalStorage('FRAPPE_FETCHED_ORG', currentOrg);
      }
      // Use the full login URL returned by API for first load
      setUrl(login_url);
      retryCountRef.current = 0;
    } catch (error) {
      LogConsole('Fetch error:', error?.message);

      // Check if we should retry
      const shouldRetry = retryCountRef.current < RETRY_ATTEMPTS;

      if (shouldRetry) {
        retryCountRef.current += 1;
        isFetchingData.current = false;

        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        const retryDelay = RETRY_DELAY_BASE * Math.pow(2, retryCountRef.current - 1);
        LogConsole(
          `Retrying fetchData (attempt ${retryCountRef.current}/${RETRY_ATTEMPTS}) after ${retryDelay}ms`
        );

        retryTimeoutRef.current = setTimeout(() => {
          fetchData();
        }, retryDelay);
        return;
      }

      // All retries exhausted - show error
      LogConsole('All retry attempts exhausted');
      removeLocalStorage('FRAPPE_APP_URL');
      removeLocalStorage('FRAPPE_FETCHED_ORG');
      removeLocalStorage('FRAPPE_APP_READY');
      setLoading(false);
      setError(true);
      retryCountRef.current = 0;
    } finally {
      isFetchingData.current = false;

      // Clear any existing loading timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      loadingTimeoutRef.current = setTimeout(() => setLoading(false), LOADING_AUTO_CLOSE_DELAY);
    }
  }, [currentOrg, urlRef.current, hasPermission]);

  const handleReceivedMessage = useCallback(
    (event) => {
      if (event.data?.source === 'FRAPPE_APP') {
        const currentUrlRequestId = searchParams.get('id');

        LogConsole('Received message from Frappe App:', event.data);

        // Optimized one-time redirect: execute only if target differs from last handled
        if (event.data?.redirectTo) {
          const target = event.data.redirectTo;
          if (lastRedirectRef.current !== target) {
            lastRedirectRef.current = target;
            if (event.data?.redirectToNewTab) {
              setTimeout(() => {
                lastRedirectRef.current = null;
              }, 2000); // Reset after 2 seconds to allow future redirects to same target
              windowOpen(target, '_blank');
            } else {
              // Only navigate if not already at the target path
              if (window.location.pathname + window.location.search !== target) {
                setTimeout(() => {
                  lastRedirectRef.current = null;
                }, 2000); // Reset after 2 seconds to allow future redirects to same target
                navigate(target, { replace: true });
              }
            }
          }
          return;
        }

        // if (event.data?.iframeHeight) setIframeHeight(event.data.iframeHeight);

        if (!getLocalStorage('FRAPPE_APP_URL')) {
          const _url = document.querySelector('#FRAPPE_APP')?.src;
          if (_url) {
            const url_break = new URL(_url);
            setLocalStorage('FRAPPE_APP_URL', url_break?.origin);
            setLocalStorage('FRAPPE_FETCHED_ORG', currentOrg);
          }
        }

        if (event.data?.closeAnyOpenedMenu) {
          window.closeOpenedMenu?.();
        }

        if (event.data?.openModal?.targetId && event.data?.openModal?.type === 'THREEDMODELS') {
          const modelId = +event.data?.openModal?.targetId;
          const link = ADMIN_MY_PRODUCTS_3DMODAL_EACH.replaceAll(':id', modelId);
          windowOpen(link, '_blank');
          // dispatch(model3dDetailsActions.show({ id: modelId }));
        }

        if (event.data?.relogging && !isReloggingInitiated.current) {
          clearDataAfterLogout();

          if (window.location.pathname.includes(REQUESTS_ROUTE)) {
            warningToast(event.data?.reason + ' Relogging...');
          }

          setTimeout(() => {
            fetchData();
          }, RELOGIN_DELAY);
        } else if (event.data?.requestId || event.data?.requestName) {
          // Update page title if request name is provided
          if (event.data?.requestName) {
            setPageTitle(event.data.requestName);
          }

          // Handle request ID from Frappe
          const frappeRequestId = event.data?.requestId;
          lastRequestIdRef.current = frappeRequestId;

          if (!openedRequestId && frappeRequestId) {
            // Only update URL if the ID is different from current URL
            if (String(currentUrlRequestId) !== String(frappeRequestId)) {
              const params = new URLSearchParams(searchParams);
              params.set('id', frappeRequestId);
              setSearchParams(params, { replace: true });
            }
          }
        } else {
          // Handle other events
          if (typeof event.data?.loading === 'boolean') {
            setLoading(event.data.loading);
            if (!getLocalStorage('FRAPPE_APP_READY', true) && !event.data.loading) {
              // Mark ready and if iframe src is still the full login URL, normalize to origin
              setLocalStorage('FRAPPE_APP_READY', true);
              const originUrl = getLocalStorage('FRAPPE_APP_URL');
              if (originUrl && !urlRef.current) {
                if (location.pathname.includes(REQUESTS_ROUTE) && show) {
                  setUrl(originUrl);
                  apiCallAttemptedRef.current = true;
                }
              }
            }
          }

          // Only clear params if explicitly requested
          if (!openedRequestId && event.data?.clearParams && currentUrlRequestId) {
            const params = new URLSearchParams(searchParams);
            if (params?.has('id')) {
              params.delete('id');
              setSearchParams(params, { replace: true });
            }
            setPageTitle('Requests');
          }
        }
      }
    },
    [searchParams, setSearchParams, location.pathname, show, currentOrg, urlRef.current]
  );

  useEffect(() => {
    window.sendDataToFrappeApp = sendDataToFrappeApp;
    window.addEventListener('message', handleReceivedMessage);

    return () => {
      // setLoading(false);

      // Cleanup timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }

      window.removeEventListener('message', handleReceivedMessage);
    };
  }, [handleReceivedMessage]);

  useEffect(() => {
    // Prefer already-authenticated origin URL first; otherwise attempt to fetch (first-time or org mismatch)
    const FRAPPE_APP_URL = getLocalStorage('FRAPPE_APP_URL');
    const FRAPPE_FETCHED_ORG = getLocalStorage('FRAPPE_FETCHED_ORG', true);
    const FRAPPE_APP_READY = getLocalStorage('FRAPPE_APP_READY', true);

    if (FRAPPE_APP_READY && FRAPPE_APP_URL && FRAPPE_FETCHED_ORG === currentOrg) {
      if (!urlRef.current && show) {
        setUrl(FRAPPE_APP_URL);
      }
      apiCallAttemptedRef.current = true;
      // sendDataToFrappeApp will be called by iframe.onLoad
      return;
    }

    // let delayTimer;
    if (
      !urlRef.current &&
      !isFetchingData.current &&
      !apiCallAttemptedRef.current
      // (!FRAPPE_FETCHED_ORG || FRAPPE_FETCHED_ORG !== currentOrg)
    ) {
      // Add delay for first-time fetch
      // delayTimer = setTimeout(() => {
        fetchData();
      // }, 500); // 500ms delay
    }

    // return () => {
    //   if (delayTimer) {
    //     clearTimeout(delayTimer);
    //   }
    // };
  }, [urlRef.current, currentOrg, iframeLoadedRef.current, show, fetchData]);

  useEffect(() => {
    const handleOrgChange = async () => {
      const FRAPPE_FETCHED_ORG = getLocalStorage('FRAPPE_FETCHED_ORG', true);
      if (urlRef.current && FRAPPE_FETCHED_ORG && FRAPPE_FETCHED_ORG !== currentOrg) {
        let isCancelled = false;
        setLoading(true);

        clearDataAfterLogout();

        if (!isCancelled) {
          fetchData();
        }

        return () => {
          isCancelled = true;
        };
      }
    };

    handleOrgChange();
  }, [currentOrg, urlRef.current]);

  useEffect(() => {
    // Only update title when on Requests route
    if (location.pathname.includes(REQUESTS_ROUTE)) {
      setTitle(pageTitle);
      // sendDataToFrappeApp();
    }
  }, [pageTitle, location.pathname]);

  // useEffect(() => {
  //   LogConsole('DEBUG___states', {
  //     FRAPPE_APP_URL: getLocalStorage('FRAPPE_APP_URL'),
  //     FRAPPE_FETCHED_ORG: getLocalStorage('FRAPPE_FETCHED_ORG', true),
  //     FRAPPE_APP_READY: getLocalStorage('FRAPPE_APP_READY', true),
  //     url,
  //     currentOrg,
  //     error,
  //   });
  // }, [url, currentOrg, error]);

  const clearDataAfterLogout = () => {
    // setUrl(null);
    // Now clear storage and reset state
    removeLocalStorage('FRAPPE_APP_URL');
    removeLocalStorage('FRAPPE_FETCHED_ORG');
    removeLocalStorage('FRAPPE_APP_READY');
    isFetchingData.current = false;
    apiCallAttemptedRef.current = false;
    retryCountRef.current = 0;
    isReloggingInitiated.current = true;
    // Send logout first while iframe still exists
    sendDataToFrappeApp({ logoutFrappe: true });
    setUrl(null);
  };

  if (!loading && error)
    return (
      <NoDataFound
        className="d-flex align-items-center justify-content-center w-100 h-100"
        height="auto">
        <h5 className="p-4 text-center">
          Something went wrong! Please try again or contact support.
        </h5>
      </NoDataFound>
    );

  return (
    <>
      {url && (
        <iframe
          key={currentOrg}
          id="FRAPPE_APP"
          src={url}
          width="100%"
          style={{
            // ...(iframeHeight ? { minHeight: iframeHeight + 'px' } : {}),
            height:
              !checkIfEmailIsVerifiedOrNot(false) && !openedRequestId
                ? 'calc(100% - 50px)'
                : '100%',
            border: 'none',
            display: (openedRequestId || isRequestsPage) && show ? 'block' : 'none',
          }}
          onLoad={() => {
            iframeLoadedRef.current = true;
            sendDataToFrappeApp();
            // setLoading(false);
          }}
          onError={() => {
            setLoading(false);
            iframeLoadedRef.current = false;
          }}></iframe>
      )}
      {show && !isGuestUser() && <My3dModelDetailsPopupController />}
      {show && (openedRequestId || isRequestsPage) && !isUpdatingUserDetails && loading && (
        <Loader
          loading={true}
          position="absolute"
          style={{
            backgroundColor: '#F6F6F6',
          }}
        />
      )}
    </>
  );
};
